<template>
  <BaseCard
    v-if="preview && preview.available"
    class="mb-6"
    data-test="billing-switch"
  >
    <h2 class="text-lg">
      A simpler way to pay
    </h2>
    <p class="mt-2 text-gray-700">
      {{ sentence }}
    </p>
    <p
      class="mt-2 text-sm text-gray-600"
      data-test="switch-terms"
    >
      Your {{ preview.plan_name }} plan stays as it is unless you switch. If you switch, your annual plan and its monthly usage billing end today; the {{ annualFee }} annual fee is not refunded; your {{ preview.unused_credits }} unused {{ preview.unused_credits === 1 ? 'credit becomes' : 'credits become' }} {{ preview.unused_credits }} free {{ preview.unused_credits === 1 ? 'candidate' : 'candidates' }} on the new pricing; after those, {{ unitAmount }} per candidate whose results you unlock, billed monthly to the card you already have on file.
    </p>
    <div class="flex flex-wrap gap-2 mt-4">
      <BaseButton
        data-test="switch-open"
        @click="showConfirm = true"
      >
        Switch to per-candidate pricing
      </BaseButton>
    </div>

    <Modal
      :open="showConfirm"
      :can-close="!switching"
      @close="showConfirm = false"
    >
      <div
        class="p-6"
        data-test="switch-confirm"
      >
        <h3 class="text-lg font-medium">
          Switch to per-candidate pricing
        </h3>
        <ul class="my-3 ml-5 space-y-1 text-sm list-disc list-outside">
          <li>Your {{ preview.plan_name }} plan and its monthly usage billing end today, not at the end of the period you paid for.</li>
          <li>The {{ annualFee }} annual fee is not refunded.</li>
          <li>Your {{ preview.unused_credits }} unused {{ preview.unused_credits === 1 ? 'credit becomes' : 'credits become' }} {{ preview.unused_credits }} free {{ preview.unused_credits === 1 ? 'candidate' : 'candidates' }} on the new pricing.</li>
          <li>After those, {{ unitAmount }} per candidate whose results you unlock, billed monthly to your card on file. Nothing is charged today.</li>
        </ul>
        <p class="inline-flex space-x-2">
          <BaseButton
            data-test="switch-confirm-button"
            :loading="switching"
            @click="switchNow()"
          >
            Yes, switch now
          </BaseButton>
          <BaseButton
            variant="salmon"
            @click="showConfirm = false"
          >
            Keep my plan
          </BaseButton>
        </p>
        <p
          v-if="error"
          class="mt-3 text-sm text-red-700"
        >
          {{ error }}
        </p>
      </div>
    </Modal>
  </BaseCard>
</template>

<script>
import Modal from '@components/Modal'

import candidateBillingApi from '@api/candidateBilling'
import { formatMinor } from '@utils/candidateBilling'

/**
 * The self-serve switch for an annual customer (Guy, 7 September 2026):
 * both subscriptions end at once, unused included credits become free
 * candidates, no refund, the card on file is reused. Every one of those is
 * stated on the card and again on the confirmation.
 */
export default {
  components: {
    Modal
  },

  data() {
    return {
      preview: null,
      showConfirm: false,
      switching: false,
      error: null
    }
  },

  computed: {
    unitAmount() {
      return this.preview && this.preview.unit_amount ? formatMinor(this.preview.unit_amount, this.preview.currency) : ''
    },

    annualFee() {
      return this.preview && this.preview.annual_fee ? formatMinor(this.preview.annual_fee, this.preview.currency) : ''
    },

    sentence() {
      if (!this.preview) {
        return ''
      }
      return `Pay ${this.unitAmount} per candidate whose results you unlock, billed monthly. No plan, no annual fee, cancel any time.`
    }
  },

  mounted() {
    candidateBillingApi.switchPreview()
      .then(preview => { this.preview = preview })
      .catch(() => { this.preview = null })
  },

  methods: {
    switchNow() {
      if (this.switching) {
        return
      }
      this.switching = true
      this.error = null
      candidateBillingApi.switchFromAnnual()
        .then(() => Promise.all([
          this.$store.dispatch('organisations/getOrganisation'),
          this.$store.dispatch('subscriptions/getSubscriptions')
        ]))
        .then(() => {
          this.showConfirm = false
          this.preview = null
        })
        .catch(() => {
          this.error = 'Sorry, we could not switch right now. Your plan is unchanged.'
        })
        .finally(() => {
          this.switching = false
        })
    }
  }
}
</script>
